import React, { useEffect, useMemo, useState } from "react";
import api, { BASE_URL } from "../../Utils/api";
import { FiCheck, FiInfo, FiStar, FiX } from "react-icons/fi";
import "./Precos.css";


const PLANOS_FALLBACK = [
  {
    id: "gratuito",
    nome: "Doc Gratuito",
    descricao: "Para conhecer o Doc e começar a organizar seus documentos.",
    mensal: 0,
    anual: 0,
    acao: "Começar grátis",
  },
  {
    id: "essencial",
    nome: "Doc Essencial",
    descricao: "Para pequenas empresas começarem a controlar seus vencimentos.",
    mensal: 19.99,
    anual: 199.9,
    acao: "Escolher Doc Essencial",
  },
  {
    id: "plus",
    nome: "Doc Plus",
    descricao: "Para empresas com mais documentos, filiais e responsáveis.",
    mensal: 39.99,
    anual: 399.9,
    acao: "Escolher Doc Plus",
    destaque: "Mais escolhido",
  },
  {
    id: "pro",
    nome: "Doc Pro",
    descricao: "Para operações maiores e com alto volume documental.",
    mensal: 79.99,
    anual: 799.9,
    acao: "Escolher Doc Pro",
  },
  {
    id: "infinity",
    nome: "Doc Infinity",
    descricao: "Para operações sem limite de cadastros e com mais armazenamento.",
    mensal: 149.99,
    anual: 1499.9,
    acao: "Escolher Doc Infinity",
  },
];

const GRUPOS_RECURSOS_FALLBACK = [
  {
    titulo: "Limites",
    recursos: [
      { nome: "Documentos ativos", valores: ["10", "500", "2.000", "5.000", "Ilimitados"], info: "Qualquer Documento Ativo!" },
      { nome: "Usuários", valores: ["1", "3", "10", "20", "Ilimitados"], info: "Usuários para Cadastro dos Documentos e Configuração do Sistema!" },
      { nome: "Destinatários de comunicados", valores: ["Ilimitados", "Ilimitados", "Ilimitados", "Ilimitados", "Ilimitados"], info: "Usuários do tipo Comunicadosão usuários que não acessam o sistema, mas recebem alertas por e-mail!" },
      { nome: "Filiais", valores: ["1", "3", "10", "30", "Ilimitados"] },
      { nome: "Fornecedores", valores: ["1", "3", "10", "30", "Ilimitados"] },
      { nome: "Armazenamento de Anexos", valores: ["50 MB", "1 GB", "5 GB", "15 GB", "30 GB"], info: "O armazenamento refe-se aos arquivos anexos dos documentos!" },
    ],
  },
  {
    titulo: "Recursos",
    recursos: [
      { nome: "Alertas de vencimento", valores: [true, true, true, true, true] },
      { nome: "Gestão de fornecedores e funcionários", valores: [true, true, true, true, true] },
      { nome: "Documentos arquivados não consomem o limite", valores: [true, true, true, true, true], info: "Ao arquivar um documento os anexos dele são excluidos, assim não consomem o limite de armazenamento!" },
    ],
  },
];

const moeda = new Intl.NumberFormat("pt-BR", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function ValorRecurso({ valor, destaque }) {
  if (typeof valor !== "boolean") {
    return <strong className={destaque ? "precos-valor-destaque" : ""}>{valor}</strong>;
  }

  return valor ? (
    <span className="precos-disponivel" aria-label="Disponível">
      <FiCheck aria-hidden="true" />
    </span>
  ) : (
    <span className="precos-indisponivel" aria-label="Não disponível">
      <FiX aria-hidden="true" />
    </span>
  );
}

function AlertaRecurso({ nome, info }) {
  if (!info) return null;

  return (
    <button
      type="button"
      className="precos-recurso-alerta"
      aria-label={`Informações sobre ${nome}`}
      title={info}
    >
      <FiInfo aria-hidden="true" />
    </button>
  );
}

function NomeRecurso({ nome, info }) {
  const ultimoEspaco = nome.lastIndexOf(" ");
  const inicio = ultimoEspaco > 0 ? nome.slice(0, ultimoEspaco) : "";
  const final = ultimoEspaco > 0 ? nome.slice(ultimoEspaco + 1) : nome;

  return (
    <span className="precos-recurso-nome">
      {inicio && `${inicio} `}
      <span className="precos-recurso-final">
        {final}
        <AlertaRecurso nome={nome} info={info} />
      </span>
    </span>
  );
}

function Precos({
  modoSelecao = false,
  onSelecionarPlano,
  onFechar,
  planoAtualCodigo = null,
  planoSelecionadoCodigo = null,
  cicloInicial = "mensal",
  renderEntrePlanosELimites = null,
}) {
  const [ciclo, setCiclo] = useState(cicloInicial === "anual" ? "anual" : "mensal");
  const [planos, setPlanos] = useState(PLANOS_FALLBACK);
  const [gruposRecursos, setGruposRecursos] = useState(GRUPOS_RECURSOS_FALLBACK);
  const [checkoutMsg, setCheckoutMsg] = useState(null);
  const [checkoutErro, setCheckoutErro] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  useEffect(() => {
    api
      .get(`${BASE_URL}/api/tabelasPrecos/vigente`)
      .then((res) => {
        const data = res.data?.data;
        if (data?.planos?.length) setPlanos(data.planos);
        if (data?.gruposRecursos?.length) {
          setGruposRecursos(
            data.gruposRecursos.map((g) => ({
              titulo: g.titulo,
              recursos: g.recursos.map((r) => ({
                nome: r.nome,
                info: r.info,
                valores: r.valores,
              })),
            }))
          );
        }
      })
      .catch(() => {
        /* mantém fallback estático */
      });
  }, []);

  useEffect(() => {
    if (modoSelecao) {
      setCiclo(cicloInicial === "anual" ? "anual" : "mensal");
    }
  }, [modoSelecao, cicloInicial]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get("checkout");
    if (status === "sucesso") setCheckoutMsg("Pagamento recebido! Seu plano será ativado em instantes.");
    if (status === "cancelado") setCheckoutErro("Checkout cancelado. Você pode tentar novamente quando quiser.");
    if (status === "expirado") setCheckoutErro("Checkout expirado. Gere um novo link de pagamento.");
  }, []);

  useEffect(() => {
    if (modoSelecao) return;

    const classesAnteriores = document.body.className;
    const paddingAnterior = document.body.style.paddingLeft;

    document.body.className = "precos-page-body";
    document.body.style.paddingLeft = "0";

    return () => {
      document.body.className = classesAnteriores;
      document.body.style.paddingLeft = paddingAnterior;
    };
  }, [modoSelecao]);

  const planosExibidos = useMemo(
    () =>
      planos.map((plano) => ({
        ...plano,
        valor: ciclo === "anual" ? plano.anual : plano.mensal,
        periodo: ciclo === "anual" ? "/ano" : "/mês",
        equivalente:
          plano.mensal === 0
            ? "gratuito para começar"
            : ciclo === "anual"
            ? `equivale a R$ ${moeda.format(plano.anual / 12)}/mês`
            : null,
      })),
    [ciclo, planos]
  );

  const seletorCiclo = (
    <div
      className={`precos-ciclo${modoSelecao ? " precos-ciclo--selecao" : ""}`}
      role="group"
      aria-label="Ciclo de cobrança"
    >
      <button
        type="button"
        className={ciclo === "mensal" ? "ativo" : ""}
        aria-pressed={ciclo === "mensal"}
        onClick={() => setCiclo("mensal")}
      >
        Mensal
      </button>
      <button
        type="button"
        className={ciclo === "anual" ? "ativo" : ""}
        aria-pressed={ciclo === "anual"}
        onClick={() => setCiclo("anual")}
      >
        Anual
        <span>2 meses grátis</span>
      </button>
    </div>
  );

  const handleEscolherPlano = async (plano, event) => {
    event.preventDefault();
    setCheckoutErro(null);
    setCheckoutMsg(null);

    if (modoSelecao) {
      if (onSelecionarPlano) {
        onSelecionarPlano({
          codigo: plano.id,
          nome: plano.nome,
          ciclo:
            ciclo === "anual"
              ? "ANUAL"
              : plano.mensal === 0 && plano.anual === 0
              ? "GRATUITO"
              : "MENSAL",
          mensal: plano.mensal,
          anual: plano.anual,
        });
      }
      return;
    }

    if (plano.mensal === 0 && plano.anual === 0) {
      window.location.href = "/login";
      return;
    }

    const token = localStorage.getItem("authToken");
    const entidadeId = localStorage.getItem("entidade_id");
    if (!token || !entidadeId) {
      window.location.href = "/login";
      return;
    }

    try {
      setCheckoutLoading(true);
      const { data: resposta } = await api.post(
        `${BASE_URL}/api/assinaturas/checkout`,
        {
          codigo_plano: plano.id,
          ciclo: ciclo === "anual" ? "ANUAL" : "MENSAL",
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            id_entidade: entidadeId,
          },
        }
      );
      const url = resposta?.data?.checkout_url;
      if (!url) throw new Error("URL de checkout não retornada.");
      window.location.href = url;
    } catch (err) {
      setCheckoutErro(
        err.response?.data?.message ||
          "Não foi possível iniciar o checkout. Tente novamente ou entre em contato com o suporte."
      );
    } finally {
      setCheckoutLoading(false);
    }
  };

  return (
    <div className={`precos-page${modoSelecao ? " precos-page--selecao" : ""}`}>
      {modoSelecao && (
        <div className="precos-selecao-bar">
          <div>
            <strong>Selecionar plano</strong>
            <span className="precos-selecao-hint">
              Escolha o ciclo (mensal ou anual), depois selecione o plano.
            </span>
          </div>
          <div className="precos-selecao-acoes">
            {seletorCiclo}
            {onFechar && (
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onFechar}>
                Fechar
              </button>
            )}
          </div>
        </div>
      )}
      <main>
        {!modoSelecao && (
        <section className="precos-hero" id="precos">
          <span className="precos-selo">
            <FiStar aria-hidden="true" />
            Planos simples e transparentes
          </span>
          <h1>
            Escolha o plano ideal para manter seus{" "}
            <span>documentos em dia</span>
          </h1>
          <p>
            Controle vencimentos, organize documentos e evolua conforme sua
            empresa crescer.
          </p>
          <p className="precos-seguranca-pagamento">
            Pagamento seguro via Asaas — dados de cartão não são armazenados no GDoc.
          </p>

          {checkoutMsg && <p className="precos-alerta precos-alerta-sucesso">{checkoutMsg}</p>}
          {checkoutErro && <p className="precos-alerta precos-alerta-erro">{checkoutErro}</p>}

          {seletorCiclo}
        </section>
        )}

        <section className="precos-comparacao" aria-label="Comparação de planos">
          <p className="precos-arraste" aria-hidden="true">
            Deslize para comparar os planos →
          </p>
          <div className="precos-tabela-scroll">
            <table className="precos-tabela">
              <thead>
                <tr>
                  <th className="precos-coluna-recurso">
                    <span>Planos</span>
                    <strong>Compare e escolha</strong>
                  </th>
                  {planosExibidos.map((plano) => {
                    const codigoPlano = String(plano.id || "").toLowerCase();
                    const selecionado =
                      planoSelecionadoCodigo &&
                      codigoPlano === String(planoSelecionadoCodigo).toLowerCase();
                    const atual =
                      planoAtualCodigo &&
                      codigoPlano === String(planoAtualCodigo).toLowerCase() &&
                      !selecionado;

                    return (
                    <th
                      key={plano.id}
                      className={[
                        plano.destaque ? "precos-plano-destaque" : "",
                        selecionado ? "precos-plano-selecionado" : "",
                        atual ? "precos-plano-atual" : "",
                      ].filter(Boolean).join(" ")}
                    >
                      <span className="precos-plano-badge-slot">
                        {plano.destaque && (
                          <span className="precos-badge-destaque">
                            {plano.destaque}
                          </span>
                        )}
                      </span>
                      <strong className="precos-plano-nome">{plano.nome}</strong>
                      <span className="precos-plano-descricao">
                        {plano.descricao}
                      </span>
                      <span className="precos-plano-valor">
                        <small>R$</small> {moeda.format(plano.valor)}
                        <small>{plano.periodo}</small>
                      </span>
                      <span className="precos-plano-equivalente">
                        {plano.equivalente || "\u00a0"}
                      </span>
                      <button
                        type="button"
                        className={`btn btn-app-primary precos-plano-acao${selecionado ? " precos-plano-acao-selecionado" : ""}`}
                        disabled={checkoutLoading}
                        onClick={(e) => handleEscolherPlano(plano, e)}
                        aria-label={
                          modoSelecao
                            ? `Selecionar ${plano.nome}`
                            : `${plano.acao}: ${plano.nome}`
                        }
                      >
                        {checkoutLoading
                          ? "Aguarde…"
                          : selecionado
                          ? "Selecionado"
                          : modoSelecao
                          ? "Selecionar"
                          : plano.acao}
                      </button>
                    </th>
                    );
                  })}
                </tr>
              </thead>

              <tbody>
                {renderEntrePlanosELimites && (
                  <tr className="precos-linha-extra">
                    <td colSpan={planos.length + 1}>
                      {renderEntrePlanosELimites()}
                    </td>
                  </tr>
                )}
                {gruposRecursos.map((grupo) => (
                  <React.Fragment key={grupo.titulo}>
                    <tr className="precos-grupo">
                      <th colSpan={planos.length + 1}>{grupo.titulo}</th>
                    </tr>
                    {grupo.recursos.map((recurso) => (
                      <tr key={recurso.nome}>
                        <th scope="row">
                          <NomeRecurso nome={recurso.nome} info={recurso.info} />
                        </th>
                        {recurso.valores.map((valor, indice) => (
                          <td
                            key={`${recurso.nome}-${planos[indice]?.id || indice}`}
                            className={
                              planos[indice]?.destaque
                                ? "precos-plano-destaque"
                                : ""
                            }
                          >
                            <ValorRecurso
                              valor={valor}
                              destaque={Boolean(planos[indice]?.destaque)}
                            />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {!modoSelecao && (
        <section className="precos-beneficios" id="beneficios">
          {[
            "Sem fidelidade",
            "Cancele quando quiser",
            "Sem taxas ocultas",
            "Pagamento mensal ou anual",
          ].map((beneficio) => (
            <span key={beneficio}>
              <FiCheck aria-hidden="true" />
              {beneficio}
            </span>
          ))}
        </section>
        )}

        {!modoSelecao && (
        <p className="precos-observacao">
          Usuários do tipo Comunicado recebem alertas, não acessam o sistema e
          são ilimitados em todos os planos.
        </p>
        )}
      </main>
    </div>
  );
}

export default Precos;
